/**
 * Admin Credit Audit Service
 *
 * CC-211-AUDIT Phase 2I: Typed client for the two Platform-Admin-only
 * Credit Completeness lineage endpoints shipped in Phase 2G (commit 256d99f):
 *
 *   GET /verascore/admin/members/:memberId/credit-completeness/history?limit=N
 *   GET /verascore/admin/members/:memberId/credit-completeness/audit/:snapshotId
 *
 * Auth: session cookie (credentials: 'include') + 'Platform Admin' role gate on server.
 * Purpose: all calls tagged 'compliance_audit' per PRIV-20.3 convention.
 *
 * Mirrors contract from api/src/handlers/admin/credit-completeness/* (Phase 2F).
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;
const PURPOSE_HEADER = 'x-request-purpose';
const PURPOSE_VALUE = 'compliance_audit';

// ============================================================================
// Types — mirror the server contract (do not extend without server changes)
// ============================================================================

/** 11-key snapshot summary returned by /history. */
export interface CreditCompletenessSnapshotSummary {
  id: string;
  created_at: string;
  overall_score: number;
  coverage_score: number;
  profile_score: number;
  model_version: string;
  scoring_pipeline_version: string;
  input_hash_prefix: string;
  trace_id: string | null;
  reasons_count: number;
  _links: { audit: string };
}

export interface CreditCompletenessHistoryResponse {
  member_id: string;
  count: number;
  limit: number;
  snapshots: CreditCompletenessSnapshotSummary[];
}

export interface CompletenessReason {
  code: string;
  text: string;
  severity: 'action' | 'info' | 'warning' | string;
}

export interface InputsEcho {
  match_ids: string[];
  resolution_ids: string[];
  active_tradeline_ids: string[];
  matchable_tradeline_ids: string[];
  satisfied_matchable_ids: string[];
}

export interface CreditCompletenessSnapshotFull {
  id: string;
  member_id: string;
  coverage_score: number;
  profile_score: number;
  overall_score: number;
  input_hash: string;
  output_json: Record<string, unknown>;
  model_version: string;
  scoring_pipeline_version: string;
  trace_id: string | null;
  inputs_echo: InputsEcho | null;
  reasons: CompletenessReason[];
  created_at: string;
}

export interface CreditCompletenessScoringEvent {
  event_id: string;
  snapshot_id: string;
  member_id: string;
  trace_id: string | null;
  pipeline_version: string;
  trigger_source: string | null;
  inputs_echo: InputsEcho | null;
  formula_weights: Record<string, unknown>;
  intermediate: Record<string, unknown>;
  reasons: CompletenessReason[];
  compute_ms: number | null;
  created_at: string;
}

export interface CreditCompletenessLineageResponse {
  snapshot: CreditCompletenessSnapshotFull;
  event: CreditCompletenessScoringEvent | null;
  lineage_present: boolean;
  degraded_reasons: string[];
}

export class AdminCreditAuditError extends Error {
  status: number;
  reason?: string;
  constructor(message: string, status: number, reason?: string) {
    super(message);
    this.name = 'AdminCreditAuditError';
    this.status = status;
    this.reason = reason;
  }
}

// ============================================================================
// API calls
// ============================================================================

function handleAuthRedirect(status: number): void {
  if (import.meta.env.DEV) console.warn(`⚠️ [adminCreditAuditService] Session/role error (${status}), redirecting to logout...`);
  window.history.replaceState({}, '', '?view=logout&reason=timeout');
  window.dispatchEvent(new PopStateEvent('popstate'));
}

async function parseFailure(response: Response): Promise<AdminCreditAuditError> {
  let reason: string | undefined;
  try {
    const body = await response.json();
    reason = body?.data?.reason || body?.message;
  } catch {
    /* non-JSON body */
  }
  return new AdminCreditAuditError(
    `HTTP ${response.status}: ${response.statusText}${reason ? ` — ${reason}` : ''}`,
    response.status,
    reason,
  );
}

export async function getCreditCompletenessHistory(
  memberId: string,
  limit = 10,
): Promise<CreditCompletenessHistoryResponse> {
  if (import.meta.env.DEV) console.log(`📡 [adminCreditAuditService] getHistory memberId=${memberId} limit=${limit}`);
  const url = `${API_BASE}/verascore/admin/members/${encodeURIComponent(memberId)}/credit-completeness/history?limit=${limit}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: PURPOSE_VALUE,
    },
    credentials: 'include',
  });
  if (response.status === 401 || response.status === 403) {
    // 403 may be legitimate role denial — bubble up rather than force-logout
    if (response.status === 401) handleAuthRedirect(response.status);
    throw await parseFailure(response);
  }
  if (!response.ok) throw await parseFailure(response);
  const body = await response.json();
  if (body?.status !== 'success') {
    throw new AdminCreditAuditError(`Unexpected response status: ${body?.status}`, response.status, body?.data?.reason);
  }
  return body.data as CreditCompletenessHistoryResponse;
}

export async function getCreditCompletenessAudit(
  memberId: string,
  snapshotId: string,
): Promise<CreditCompletenessLineageResponse> {
  if (import.meta.env.DEV) console.log(`📡 [adminCreditAuditService] getAudit memberId=${memberId} snapshotId=${snapshotId}`);
  const url = `${API_BASE}/verascore/admin/members/${encodeURIComponent(memberId)}/credit-completeness/audit/${encodeURIComponent(snapshotId)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: PURPOSE_VALUE,
    },
    credentials: 'include',
  });
  if (response.status === 401 || response.status === 403) {
    if (response.status === 401) handleAuthRedirect(response.status);
    throw await parseFailure(response);
  }
  if (!response.ok) throw await parseFailure(response);
  const body = await response.json();
  if (body?.status !== 'success') {
    throw new AdminCreditAuditError(`Unexpected response status: ${body?.status}`, response.status, body?.data?.reason);
  }
  return body.data as CreditCompletenessLineageResponse;
}

/** Client-side UUID v4 (loose) shape validator — matches backend regex on admin routes. */
export const MEMBER_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidMemberUuid(value: string): boolean {
  return MEMBER_UUID_REGEX.test(value.trim());
}
